import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Search, UserPlus, MapPin } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";

const ROLES = [
  { value: "all", label: "All roles" },
  { value: "composer", label: "Composer" },
  { value: "songwriter", label: "Songwriter" },
  { value: "both", label: "Both" },
];

export default function Network() {
  const [users, setUsers] = useState([]);
  const [conns, setConns] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [country, setCountry] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (role !== "all") params.role = role;
      if (country.trim()) params.country = country.trim();
      const [u, c] = await Promise.all([
        api.get("/users", { params }),
        api.get("/connections"),
      ]);
      setUsers(u.data);
      setConns(c.data);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [role]);

  const onSearch = (e) => { e.preventDefault(); load(); };

  const connStatus = (uid) => {
    const c = conns.find((x) => x.from_user_id === uid || x.to_user_id === uid);
    if (!c) return null;
    if (c.status === "accepted") return "connected";
    return c.direction === "outgoing" ? "sent" : "received";
  };

  const sendRequest = async (uid, message) => {
    try {
      await api.post("/connections", { to_user_id: uid, message });
      toast.success("Connection request sent");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0C10] pt-28 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        <div className="overline">The Society</div>
        <h1 className="font-display text-4xl lg:text-6xl mt-2 tracking-tight">
          Discover <span className="text-[#7DA3D9]">fellow creators</span>
        </h1>
        <p className="text-slate-400 mt-3 max-w-2xl">
          Composers and songwriters from around the world. Send a request and start a conversation.
        </p>

        <form onSubmit={onSearch} className="glass-card p-5 mt-10 grid md:grid-cols-12 gap-3 items-center">
          <div className="md:col-span-5 relative">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              className="input-dark pl-10"
              placeholder="Search by name or bio…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="network-search-input"
            />
          </div>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="input-dark md:col-span-3"
            data-testid="network-role-filter"
          >
            {ROLES.map((r) => (
              <option key={r.value} value={r.value} className="bg-[#0A0C10]">{r.label}</option>
            ))}
          </select>
          <input
            className="input-dark md:col-span-3"
            placeholder="Country…"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            data-testid="network-country-filter"
          />
          <button type="submit" className="btn-gold md:col-span-1 text-sm" data-testid="network-search-btn">Search</button>
        </form>

        {loading ? (
          <div className="text-center py-20 overline" data-testid="network-loading">Loading members…</div>
        ) : users.length === 0 ? (
          <div className="text-center py-20 text-slate-400" data-testid="network-empty">No members match your filters.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="network-grid">
            {users.map((u) => (
              <UserCard key={u.id} user={u} status={connStatus(u.id)} onConnect={sendRequest} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UserCard({ user, status, onConnect }) {
  const [msg, setMsg] = useState("");
  const [open, setOpen] = useState(false);
  const submit = async () => {
    await onConnect(user.id, msg);
    setMsg("");
    setOpen(false);
  };

  return (
    <div className="glass-card p-7 fade-up" data-testid={`user-card-${user.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#7DA3D9]/40 to-[#1E293B] border border-white/10 flex items-center justify-center text-xl font-display text-[#B8D0EE]">
          {user.name?.[0]?.toUpperCase() || "?"}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-xl truncate">{user.name}</h3>
          <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
            <span className="capitalize">{user.role}</span>
            {user.country && (
              <>
                <span>·</span>
                <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{user.country}</span>
              </>
            )}
          </div>
        </div>
      </div>
      {user.bio && <p className="text-sm text-slate-300 mt-4 line-clamp-3">{user.bio}</p>}
      {user.genres?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-4">
          {user.genres.slice(0, 3).map((g) => (
            <span key={g} className="px-2 py-0.5 rounded-full text-[10px] border border-white/10 bg-white/5 uppercase tracking-wider">
              {g}
            </span>
          ))}
        </div>
      )}
      <div className="divider-gold my-5" />
      {status === "connected" ? (
        <div className="text-center text-sm text-[#7DA3D9]" data-testid={`status-connected-${user.id}`}>✓ Connected</div>
      ) : status === "sent" ? (
        <div className="text-center text-sm text-slate-400" data-testid={`status-sent-${user.id}`}>Request sent</div>
      ) : status === "received" ? (
        <div className="text-center text-sm text-slate-400">Awaiting your response →</div>
      ) : (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-ghost-gold w-full inline-flex items-center justify-center gap-2 text-sm" data-testid={`connect-btn-${user.id}`}>
              <UserPlus className="w-4 h-4" /> Connect
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0C10]/95 border-white/10 backdrop-blur-xl">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Connect with {user.name}</DialogTitle>
              <DialogDescription className="text-slate-400">
                Add a personal note to your invitation (optional).
              </DialogDescription>
            </DialogHeader>
            <textarea
              className="input-dark min-h-[120px] resize-none"
              placeholder="Hi! I love your work on…"
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              data-testid={`connect-message-${user.id}`}
            />
            <DialogFooter>
              <button className="btn-ghost-gold text-sm" onClick={() => setOpen(false)}>Cancel</button>
              <button className="btn-gold text-sm" onClick={submit} data-testid={`send-request-${user.id}`}>Send invitation</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
